import {decorate, observable, action, computed} from 'mobx';
import flattenConfigurationModel from '../flattenConfigurationModel';
import IssueExtenions from '../components/common/IssueExtensions';

class Store {
    availableSteps = []
    issues = {issues: []}
    formModel = {}
    formModelRev = 0
    heatExchangerOutputs = []
    dhwHeatExchangerOutput = null
    canSaveConfiguration = false
    recentlyChangedField = null
    dirty = false
    editableMode = true
    isCustom = false

    duringSave = false
    duringCopy = false
    duringDelete = false

    generalInformationDuringEdit = false
    filesDuringEdit = false
    commentDuringEdit = false

    canSave = () => !(this.generalInformationDuringEdit || this.filesDuringEdit || this.commentDuringEdit);

    hasIssues = () => this.issues.issues.length > 0;

    hasIssuesAsForceToChange = () => IssueExtenions.hasForceToChange(this.issues.issues);

    setFilesDuringEdit = duringEdit => this.filesDuringEdit = duringEdit;

    setCommentDuringEdit = value => this.commentDuringEdit = value;

    setGeneralInformationDuringEdit = duringEdit => this.generalInformationDuringEdit = duringEdit;

    setDuringSave = value => this.duringSave = value;

    setDirty(dirty) {
        this.dirty = dirty;
    }

    setIsCustom(value) {
        this.isCustom = value;
    }


    getIsCustom() {

        return this.isCustom;
    }

    setAvailableSteps(ids) {
        this.availableSteps = ids
    }

    getAvailableSteps() {
        return this.formModel.configuratorSteps;
    }

    isStepAvailable(id) {
        return Boolean(this.availableSteps.find(x => x.slug.toLowerCase() === id.toLowerCase()))
    }

    setFormModel(model) {
        this.formModel = model
        this.setDHWCalculationOutput(model.dhw)
        for (let i = 1; i <= 3; i++)
            this.setHeatingCalculationOutput(i, model['heating' + i])
    }

    incrementFormModelVersion() {
        this.formModelRev++
    }

    getFormModelVersion() {
        return this.formModelRev
    }

    getDiagram() {
        return this.formModel.diagram
    }

    setFormModelValue(key, value) {
        let path = key.split('.')
        let current = this.formModel
        for (let i = 0; i < path.length; i++) {
            current = current[path[i]]
        }
        current.value = value
    }

    getFormModelProperty(path, propertyName) {
        path = path.split('.')
        let current = this.formModel
        for (let i = 0; i < path.length; i++) {
            if (!current) {
                return undefined;
            }

            current = current[path[i]]
        }

        if (!current) {
            return undefined;
        }

        if (propertyName in current) {
            return current[propertyName]
        }

        return undefined
    }

    setFormModelProperty(path, propertyName, value) {
        path = path.split('.')
        let current = this.formModel
        for (let i = 0; i < path.length; i++) {
            if (!current) {
                return;
            }

            current = current[path[i]]
        }

        current[propertyName] = value;
    }

    setCanSaveConfiguration(can) {
        this.canSaveConfiguration = can
    }

    setHeatingCalculationOutput(index, data) {
        this.heatExchangerOutputs[index - 1] = data
    }

    getHeatingCalculationOutput(index) {
        if (index - 1 < this.heatExchangerOutputs.length) {
            return this.heatExchangerOutputs[index - 1]
        }

        return null
    }

    getHeatingTWSCalculationOutput() {
        return this.formModel.heatingTWS ? this.formModel.heatingTWS.heatExchangerOutput : null
    }

    getHeatingTWSCalculationOutputVisible() {
        return this.formModel.heatingTWS && this.formModel.heatingTWS.heatExchangerOutputVisible;
    }

    getHeatingCalculationOutputVisible = () => this.formModel.heating1 && this.formModel.heating1.heatExchangerOutputVisible

    getEESLevel() {
        if (this.formModel.ees && this.formModel.ees.isEESVisible)
            return this.formModel.ees

    }

    getPricing() {
        if (this.formModel.topArticleNumberPricing.items.items.length > 0) {
            return this.formModel.topArticleNumberPricing;
        }

        return this.formModel.pricing
    }

    setDHWCalculationOutput(data) {
        this.dhwHeatExchangerOutput = data
    }

    getDHWCalculationOutput() {
        return this.dhwHeatExchangerOutput
    }

    getDHWCalculationOutputVisible = () => this.formModel.dhw && this.formModel.dhw.heatExchangerOutputVisible

    setHeatingName(index, name) {
        this.formModel[`heating${index}`].name.value = name
    }

    setDHWName(name) {
        this.formModel[`dhw`].name.value = name
    }

    getProductType() {
        if (this.formModel.unitType) {
            return this.formModel.unitType.productType.items.find(x => x.value === this.formModel.unitType.productType.value)
        }

        return null
    }

    getTransformedFormModel() {
        return flattenConfigurationModel(this.formModel);
    }

    setIssues(issues) {
        this.issues = issues
    }

    updateAfterSave(data) {
        for (const [key, value] of Object.entries(data.files.temporaryIdStorageId)) {
            const file = this.formModel.files.items.find(f => f.fileId === key);
            if (file) {
                file.fileId = value;
            }
        }
    }

    setShowExcludedModules(value) {
        this.formModel.pricing.showExcludedModules = value;
    }
}

decorate(Store, {
    availableSteps: observable,
    formModel: observable,
    formModelRev: observable,
    heatExchangerOutputs: observable,
    dhwHeatExchangerOutput: observable,
    canSaveConfiguration: observable,
    issues: observable,
    recentlyChangedField: observable,
    filesDuringEdit: observable,
    commentDuringEdit: observable,
    generalInformationDuringEdit: observable,
    dirty: observable,

    duringSave: observable,
    duringCopy: observable,
    duringDelete: observable,

    setDirty: action,
    setAvailableSteps: action,
    setFormModel: action,
    incrementFormModelVersion: action,
    getFormModelVersion: action,
    setIssues: action,
    setFilesDuringEdit: action,
    setCommentDuringEdit: action,
    setGeneralInformationDuringEdit: action
})

const store = new Store();
export default store;